import { useParams, Link } from "react-router-dom";
import { Phone, MapPin, Clock, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import SeoHead, { localBusinessJsonLd, faqJsonLd, breadcrumbJsonLd } from "@/components/SeoHead";
import QuoteForm from "@/components/QuoteForm";
import ServicesGrid from "@/components/ServicesGrid";
import Faq from "@/components/Faq";
import CtaBanner from "@/components/CtaBanner";
import NotFound from "@/pages/NotFound";
import { findCityBySlug, CITIES } from "@/data/cities";
import { SITE, HERO_IMAGES, FAQ_GENERAL } from "@/data/siteData";

export default function CityPage() {
  const { slug } = useParams();
  const city = findCityBySlug(slug);
  if (!city) return <NotFound />;

  const title = `${city.h1Variant} (${city.postal}) - ${SITE.name}`;
  const desc = `${city.h1Variant} pour restaurants et cuisines professionnelles. ${city.intro.slice(0, 110)}... Intervention 7j/7. ☎ ${SITE.phoneDisplay}.`;

  const cityFaqs = [
    {
      q: `Intervenez-vous rapidement à ${city.name} ?`,
      a: `Oui, ${city.name} (${city.postal}) fait partie de nos zones d'intervention prioritaires. Située à environ ${city.distance} de notre base à Melun, nous intervenons généralement sous 24h, et en urgence 7j/7 24h/24 au ${SITE.phoneDisplay}.`,
    },
    {
      q: `Quels types d'établissements à ${city.name} sollicitent vos services ?`,
      a: `À ${city.name}, nous travaillons avec restaurants traditionnels, brasseries, pizzerias, sushi bars, fast-foods, hôtels-restaurants, cuisines centrales, cantines scolaires et collectivités situés notamment ${city.notableSpots}.`,
    },
    {
      q: `Quel est le tarif d'un dégraissage de hotte à ${city.name} ?`,
      a: `Le tarif dépend de la taille de la hotte, de la longueur de gaine et du niveau d'encrassement. Nous établissons un devis gratuit transparent. Nos clients à ${city.name} bénéficient d'une grille tarifaire compétitive grâce à la proximité géographique.`,
    },
    {
      q: `Délivrez-vous une attestation officielle à ${city.name} ?`,
      a: `Oui, chaque intervention à ${city.name} donne lieu à la remise d'une attestation officielle de dégraissage conforme à l'arrêté du 25 juin 1980. Document exigible par votre assurance et la DDPP de Seine-et-Marne.`,
    },
    ...FAQ_GENERAL.slice(0, 2),
  ];

  const jsonLd = [
    localBusinessJsonLd({ areaServed: [city.name], name: `${SITE.name} - ${city.name}` }),
    faqJsonLd(cityFaqs),
    breadcrumbJsonLd([
      { name: "Accueil", path: "/" },
      { name: "Zones", path: "/zones" },
      { name: city.name, path: `/${city.slug}` },
    ]),
  ];

  const nearbyCities = CITIES
    .filter((c) => c.slug !== city.slug)
    .slice(0, 6);

  return (
    <>
      <SeoHead title={title} description={desc} canonicalPath={`/${city.slug}`} jsonLd={jsonLd} image={HERO_IMAGES.kitchen} />

      {/* Hero local */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 dh-radial-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-16 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <nav className="flex items-center gap-2 text-sm text-slate-500 mb-5">
              <Link to="/" className="hover:text-[#00367e]">Accueil</Link>
              <span>/</span>
              <Link to="/zones" className="hover:text-[#00367e]">Zones</Link>
              <span>/</span>
              <span className="text-[#00367e] font-semibold">{city.name}</span>
            </nav>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full bg-[#f1f5fb] text-[#00367e] text-sm font-semibold uppercase tracking-wider">
              <MapPin size={14} /> {city.name} · {city.postal}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#00367e]">
              {city.h1Variant}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">{city.intro}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${SITE.phoneTel}`}
                data-testid={`city-call-${city.slug}`}
                className="dh-cta-shine inline-flex items-center justify-center gap-2 px-7 py-5 rounded-full bg-[#00367e] hover:bg-[#066ec4] text-white font-extrabold text-lg shadow-xl transition-all"
              >
                <Phone size={20} /> APPELER {SITE.phoneDisplay}
              </a>
              <a
                href="#devis"
                className="inline-flex items-center justify-center gap-2 px-7 py-5 rounded-full bg-white border-2 border-[#00367e] text-[#00367e] hover:bg-[#00367e] hover:text-white font-extrabold text-lg transition-all"
              >
                Devis gratuit
              </a>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              <Pill icon={Clock} text="7j/7 · 24h/24" />
              <Pill icon={ShieldCheck} text="Attestation officielle" />
              <Pill icon={CheckCircle2} text={`À ${city.distance} de Melun`} />
            </div>
          </div>

          <div className="lg:col-span-5">
            <img
              src={HERO_IMAGES.kitchen}
              alt={`Cuisine professionnelle à ${city.name}`}
              width="900" height="900"
              loading="eager"
              className="w-full rounded-3xl shadow-[0_30px_60px_rgba(0,54,126,0.18)] object-cover aspect-square"
            />
          </div>
        </div>
      </section>

      {/* Local content section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#00367e] leading-[1.1] mb-6">
            Pourquoi choisir un spécialiste local à {city.name} ?
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-5">
            <p>
              Faire appel à <strong>{SITE.name}</strong> pour le dégraissage de votre hotte à <strong>{city.name} ({city.postal})</strong>, c'est choisir un prestataire local, réactif et certifié. Notre proximité géographique nous permet d'intervenir rapidement sur tout le secteur de {city.name}, notamment {city.notableSpots}.
            </p>
            <p>
              Tous nos techniciens sont formés au protocole strict de dégraissage des hottes professionnelles : démontage et nettoyage haute pression des filtres, dégraissage chimique de la hotte intérieure et extérieure, nettoyage des gaines d'extraction, vérification du caisson moteur et du rejet en toiture. Chaque intervention à {city.name} se conclut par la remise d'une <strong>attestation officielle</strong> conforme à l'arrêté du 25 juin 1980, exigée par votre assurance et la DDPP de Seine-et-Marne.
            </p>
            <p>
              <strong>{city.h1Variant}</strong> est une opération qui doit être réalisée régulièrement, en moyenne 2 à 4 fois par an selon le type de cuisson pratiqué (friture, grillade, plancha, fours à pizza...). Une hotte non entretenue représente le premier facteur d'incendie en cuisine professionnelle : 30% des sinistres en restauration ont pour origine une accumulation de graisse dans la hotte ou les gaines.
            </p>
            <p>
              À {city.name} et dans toute la Seine-et-Marne, nous proposons également des <strong>contrats d'entretien</strong> annuels avec planification automatique de vos interventions. Vous bénéficiez d'un tarif préférentiel et d'un suivi digital de l'historique de vos prestations.
            </p>
          </div>
        </div>
      </section>

      <ServicesGrid title={<>Nos services à {city.name}</>} subtitle={`Découvrez l'ensemble de nos prestations disponibles à ${city.name} (${city.postal}) et dans tout le secteur de Melun.`} />

      <CtaBanner
        title={`Dégraissage de hotte à ${city.name} : devis gratuit`}
        subtitle={`Appelez-nous pour une intervention rapide à ${city.name} et alentours.`}
      />

      {/* Nearby cities */}
      <section className="bg-[#f8fafc] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm uppercase tracking-[0.25em] text-[#066ec4] font-bold mb-3">Maillage local</div>
          <h2 className="text-3xl font-black text-[#00367e] mb-8">Nous intervenons aussi près de {city.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {nearbyCities.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                className="group flex items-center justify-between bg-white rounded-2xl p-3.5 border border-slate-200 hover:border-[#066ec4] hover:bg-[#00367e] hover:text-white transition-all"
              >
                <span className="font-semibold text-[#00367e] group-hover:text-white text-sm">{c.name}</span>
                <ArrowRight size={14} className="text-slate-400 group-hover:text-white" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Faq items={cityFaqs} title={<>FAQ {city.name}</>} />
      <QuoteForm preselectedCity={city.name} />
    </>
  );
}

const Pill = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-100 shadow-[0_4px_14px_rgba(0,54,126,0.05)] text-[#00367e] font-semibold text-sm">
    <Icon size={16} className="text-[#066ec4]" /> {text}
  </div>
);
